import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Papa from "papaparse";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Eye, EyeOff, Copy, Check, Upload, Download,
  ExternalLink, Search, Shield, UserPlus, X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import {
  META_STATUSES, META_ACCESS_TYPES, META_KINDS,
  type MetaAccount, type MetaAccountInput, type MetaStatus, type MetaKind,
  type MetaAccessRow,
  currentCanAccessMetaIds, listMetaAccounts, upsertMetaAccount, deleteMetaAccount,
  decryptMetaSecret, listMetaAccess, grantMetaAccess, revokeMetaAccess,
  mapCsvHeader, insertEncryptedMetaAccount,
} from "@/lib/meta-ids";

export const Route = createFileRoute("/_app/meta-ids")({
  component: MetaIdsPage,
  head: () => ({
    meta: [{ title: "Meta IDs Manager" }, { name: "description", content: "Facebook / Meta account credentials" }],
  }),
});

const STATUS_BADGE: Record<MetaStatus, string> = {
  Active: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
  Banned: "bg-rose-50 text-rose-700 ring-1 ring-rose-200",
  Restricted: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
  Disabled: "bg-zinc-100 text-zinc-600 ring-1 ring-zinc-200",
  Unknown: "bg-slate-100 text-slate-600 ring-1 ring-slate-200",
};

function MetaIdsPage() {
  const { role } = useAuth();
  const isAdmin = role === "admin";
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: canAccess, isLoading: accessLoading } = useQuery({
    queryKey: ["meta-ids-access-current"],
    queryFn: currentCanAccessMetaIds,
  });

  useEffect(() => {
    if (!accessLoading && canAccess === false) {
      navigate({ to: "/dashboard" });
    }
  }, [accessLoading, canAccess, navigate]);

  const { data: accounts = [], isLoading } = useQuery({
    queryKey: ["meta-accounts"],
    queryFn: listMetaAccounts,
    enabled: !!canAccess,
  });

  const [kindFilter, setKindFilter] = useState<MetaKind | "all">("all");
  const [statusFilter, setStatusFilter] = useState<MetaStatus | "all">("all");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<MetaAccount | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<MetaAccount | null>(null);
  const [accessOpen, setAccessOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const counts = useMemo(() => {
    const c = { all: accounts.length, main: 0, child: 0 };
    for (const a of accounts) c[a.kind]++;
    return c;
  }, [accounts]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return accounts.filter((a) => {
      if (kindFilter !== "all" && a.kind !== kindFilter) return false;
      if (statusFilter !== "all" && a.status !== statusFilter) return false;
      if (!q) return true;
      return (
        a.profile_name?.toLowerCase().includes(q) ||
        a.fb_email?.toLowerCase().includes(q) ||
        (a.adspower_profile ?? "").toLowerCase().includes(q) ||
        (a.allotted_to ?? "").toLowerCase().includes(q)
      );
    });
  }, [accounts, kindFilter, statusFilter, search]);

  const deleteMut = useMutation({
    mutationFn: deleteMetaAccount,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["meta-accounts"] });
      toast.success("Account deleted");
      setDeleteTarget(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  async function handleCsvFile(file: File) {
    const text = await file.text();
    Papa.parse(text, {
      header: true,
      skipEmptyLines: true,
      complete: async (res) => {
        const fields = (res.meta.fields ?? []) as string[];
        const mapped: Record<string, string> = {};
        for (const f of fields) {
          const k = mapCsvHeader(f);
          if (k) mapped[f] = k;
        }
        let ok = 0, skipped = 0, failed = 0;
        for (const raw of res.data as Record<string, string>[]) {
          const row: Partial<MetaAccountInput> = { kind: "main", status: "Unknown" };
          for (const [csvKey, dbKey] of Object.entries(mapped)) {
            const v = (raw[csvKey] ?? "").toString().trim();
            if (!v) continue;
            (row as Record<string, unknown>)[dbKey] = v;
          }
          if (!row.profile_name || !row.fb_email) { skipped++; continue; }
          if (row.status && !META_STATUSES.includes(row.status as MetaStatus)) row.status = "Unknown";
          if (row.access_type && !META_ACCESS_TYPES.includes(row.access_type as never)) row.access_type = null;
          if (row.kind && !META_KINDS.includes(row.kind as MetaKind)) row.kind = "main";
          try {
            await upsertMetaAccount(row as MetaAccountInput);
            ok++;
          } catch {
            failed++;
          }
        }
        qc.invalidateQueries({ queryKey: ["meta-accounts"] });
        toast.success(`Imported ${ok}. Skipped ${skipped}. Failed ${failed}.`);
      },
    });
  }

  function downloadTemplate() {
    const headers = [
      "Profile Name","Date of Birth","Facebook Email","FB Password","Recovery Email",
      "Recovery Password","Status","Primary 2FA","Secondary 2FA","AdsPower Profile",
      "Created BM ID","Received BM ID","Received Access ID","Allotted To","Access Type","Kind",
    ];
    const csv = headers.join(",") + "\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "meta-ids-template.csv"; a.click();
    URL.revokeObjectURL(url);
  }

  if (accessLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  }
  if (!canAccess) {
    return null; // redirecting
  }

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Meta IDs Manager</h1>
          <p className="text-sm text-muted-foreground">
            {accounts.length} account{accounts.length === 1 ? "" : "s"} · sensitive fields encrypted at rest
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" asChild>
            <a href="https://2fa.live/" target="_blank" rel="noreferrer">
              <ExternalLink className="h-4 w-4 mr-1" /> Open 2FA Tool
            </a>
          </Button>
          <Button variant="outline" onClick={downloadTemplate}>
            <Download className="h-4 w-4 mr-1" /> Template
          </Button>
          <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
            <Upload className="h-4 w-4 mr-1" /> Import CSV
          </Button>
          <input
            ref={fileInputRef} type="file" accept=".csv" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleCsvFile(f); e.target.value = ""; }}
          />
          {isAdmin && (
            <Button variant="outline" onClick={() => setAccessOpen(true)}>
              <Shield className="h-4 w-4 mr-1" /> Manage Access
            </Button>
          )}
          <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4 mr-1" /> Add Account
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-md border border-border bg-white p-0.5">
          {(["all","main","child"] as const).map((k) => (
            <button
              key={k}
              onClick={() => setKindFilter(k)}
              className={`px-3 py-1.5 text-xs font-medium rounded ${
                kindFilter === k ? "bg-violet-600 text-white" : "text-muted-foreground hover:bg-muted"
              }`}
            >
              {k === "all" ? "All" : k === "main" ? "Main" : "Child"}
              <span className="ml-1.5 opacity-80">({k === "all" ? counts.all : counts[k]})</span>
            </button>
          ))}
        </div>

        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, AdsPower, allotted…"
            className="pl-8 w-[280px]"
          />
        </div>

        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as MetaStatus | "all")}>
          <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {META_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-border bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-[1500px] w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="sticky left-0 z-10 bg-muted/50 text-left px-3 py-2 min-w-[180px] border-r border-border">Profile Name</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">FB Email</th>
                <th className="text-left px-3 py-2">FB Password</th>
                <th className="text-left px-3 py-2">Recovery Email</th>
                <th className="text-left px-3 py-2">Recovery Pwd</th>
                <th className="text-left px-3 py-2">2FA</th>
                <th className="text-left px-3 py-2">AdsPower</th>
                <th className="text-left px-3 py-2">Created BM</th>
                <th className="text-left px-3 py-2">Received BM</th>
                <th className="text-left px-3 py-2">Allotted To</th>
                <th className="text-left px-3 py-2">Access Type</th>
                <th className="text-left px-3 py-2 sticky right-0 bg-muted/50 border-l border-border">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={13} className="px-3 py-8 text-center text-muted-foreground">Loading…</td></tr>
              )}
              {!isLoading && filtered.length === 0 && (
                <tr><td colSpan={13} className="px-3 py-8 text-center text-muted-foreground">No accounts</td></tr>
              )}
              {filtered.map((a) => (
                <tr key={a.id} className="border-t border-border hover:bg-muted/30">
                  <td className="sticky left-0 z-10 bg-white px-3 py-2 font-medium border-r border-border">
                    <div className="flex flex-col">
                      <span>{a.profile_name}</span>
                      <span className="text-[10px] uppercase text-muted-foreground">{a.kind}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[a.status]}`}>
                      {a.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{a.fb_email}</td>
                  <SecretCell accountId={a.id} encrypted={a.fb_password} />
                  <td className="px-3 py-2 text-muted-foreground">{a.recovery_email ?? "—"}</td>
                  <SecretCell accountId={a.id} encrypted={a.recovery_password} />
                  <SecretCell accountId={a.id} encrypted={a.primary_2fa} extraLink="https://2fa.live/" />
                  <td className="px-3 py-2 text-muted-foreground">{a.adspower_profile ?? "—"}</td>
                  <td className="px-3 py-2 text-muted-foreground">{a.created_bm_id ?? "—"}</td>
                  <td className="px-3 py-2 text-muted-foreground">{a.received_bm_id ?? "—"}</td>
                  <td className="px-3 py-2 text-muted-foreground">{a.allotted_to ?? "—"}</td>
                  <td className="px-3 py-2 text-muted-foreground">{a.access_type ?? "—"}</td>
                  <td className="px-3 py-2 sticky right-0 bg-white border-l border-border">
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(a)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(a)}>
                        <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {(addOpen || editing) && (
        <AccountFormDialog
          existing={editing}
          open={addOpen || !!editing}
          onClose={() => { setAddOpen(false); setEditing(null); }}
          onSaved={() => qc.invalidateQueries({ queryKey: ["meta-accounts"] })}
        />
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Meta account?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes <b>{deleteTarget?.profile_name}</b>. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 hover:bg-rose-700"
              onClick={() => deleteTarget && deleteMut.mutate(deleteTarget.id)}
            >Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {isAdmin && accessOpen && (
        <ManageAccessDialog onClose={() => setAccessOpen(false)} />
      )}
    </div>
  );
}

function SecretCell({ accountId, encrypted, extraLink }: { accountId: string; encrypted: string | null; extraLink?: string }) {
  const [revealed, setRevealed] = useState<string | null>(null);
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);

  async function reveal() {
    if (!encrypted) return;
    if (!revealed) {
      try {
        const v = await decryptMetaSecret(accountId, encrypted);
        setRevealed(v);
        setShow(true);
      } catch (e) {
        toast.error((e as Error).message);
      }
    } else {
      setShow((s) => !s);
    }
  }
  async function copy() {
    try {
      const v = revealed ?? await decryptMetaSecret(accountId, encrypted);
      if (!v) return;
      setRevealed(v);
      await navigator.clipboard.writeText(v);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  return (
    <td className="px-3 py-2">
      <div className="flex items-center gap-1.5">
        <span className="font-mono text-xs">
          {encrypted ? (show && revealed ? revealed : "••••••") : "—"}
        </span>
        {encrypted && (
          <>
            <button onClick={reveal} className="text-muted-foreground hover:text-foreground" title={show ? "Hide" : "Reveal"}>
              {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </button>
            <button onClick={copy} className="text-muted-foreground hover:text-foreground" title="Copy">
              {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
            {extraLink && (
              <a href={extraLink} target="_blank" rel="noreferrer" className="text-violet-600 hover:underline" title="Open 2FA Tool">
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </>
        )}
      </div>
    </td>
  );
}

function AccountFormDialog({
  existing, open, onClose, onSaved,
}: { existing: MetaAccount | null; open: boolean; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<MetaAccountInput>(() => ({
    id: existing?.id,
    kind: existing?.kind ?? "main",
    profile_name: existing?.profile_name ?? "",
    dob: existing?.dob ?? null,
    fb_email: existing?.fb_email ?? "",
    fb_password: undefined,
    recovery_email: existing?.recovery_email ?? null,
    recovery_password: undefined,
    status: existing?.status ?? "Unknown",
    primary_2fa: undefined,
    secondary_2fa: undefined,
    adspower_profile: existing?.adspower_profile ?? "",
    created_bm_id: existing?.created_bm_id ?? "",
    received_bm_id: existing?.received_bm_id ?? "",
    received_bm_access_id: existing?.received_bm_access_id ?? "",
    allotted_to: existing?.allotted_to ?? "",
    access_type: existing?.access_type ?? null,
  }));
  const [saving, setSaving] = useState(false);

  function up<K extends keyof MetaAccountInput>(k: K, v: MetaAccountInput[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function save() {
    if (!form.profile_name.trim() || !form.fb_email.trim()) {
      toast.error("Profile Name and FB Email are required");
      return;
    }
    setSaving(true);
    try {
      await upsertMetaAccount(form, existing);
      toast.success(existing ? "Account updated" : "Account added");
      onSaved();
      onClose();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{existing ? "Edit Meta Account" : "Add Meta Account"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <Section title="Account Credentials">
            <Row>
              <Field label="Kind">
                <Select value={form.kind} onValueChange={(v) => up("kind", v as MetaKind)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="main">Main</SelectItem>
                    <SelectItem value="child">Child</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Profile Name *">
                <Input value={form.profile_name} onChange={(e) => up("profile_name", e.target.value)} />
              </Field>
            </Row>
            <Row>
              <Field label="Date of Birth">
                <Input type="date" value={form.dob ?? ""} onChange={(e) => up("dob", e.target.value || null)} />
              </Field>
              <Field label="Status">
                <Select value={form.status} onValueChange={(v) => up("status", v as MetaStatus)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {META_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </Row>
            <Row>
              <Field label="Facebook Email *">
                <Input value={form.fb_email} onChange={(e) => up("fb_email", e.target.value)} />
              </Field>
              <Field label={existing ? "FB Password (leave blank to keep)" : "FB Password"}>
                <Input type="password" value={form.fb_password ?? ""} onChange={(e) => up("fb_password", e.target.value)} />
              </Field>
            </Row>
            <Row>
              <Field label="Recovery Email">
                <Input value={form.recovery_email ?? ""} onChange={(e) => up("recovery_email", e.target.value)} />
              </Field>
              <Field label={existing ? "Recovery Password (leave blank to keep)" : "Recovery Password"}>
                <Input type="password" value={form.recovery_password ?? ""} onChange={(e) => up("recovery_password", e.target.value)} />
              </Field>
            </Row>
          </Section>

          <Section title="2FA & Security">
            <Row>
              <Field label={
                <span className="flex items-center gap-2">
                  Primary 2FA Secret
                  <a href="https://2fa.live/" target="_blank" rel="noreferrer" className="text-violet-600 text-xs hover:underline inline-flex items-center gap-0.5">
                    2fa.live <ExternalLink className="h-3 w-3" />
                  </a>
                </span>
              }>
                <Input type="password" value={form.primary_2fa ?? ""} onChange={(e) => up("primary_2fa", e.target.value)} placeholder={existing ? "Leave blank to keep" : ""} />
              </Field>
              <Field label="Secondary 2FA Secret">
                <Input type="password" value={form.secondary_2fa ?? ""} onChange={(e) => up("secondary_2fa", e.target.value)} placeholder={existing ? "Leave blank to keep" : ""} />
              </Field>
            </Row>
          </Section>

          <Section title="AdsPower Environment">
            <Row>
              <Field label="AdsPower Profile">
                <Input value={form.adspower_profile ?? ""} onChange={(e) => up("adspower_profile", e.target.value)} />
              </Field>
              <Field label="Allotted To">
                <Input value={form.allotted_to ?? ""} onChange={(e) => up("allotted_to", e.target.value)} />
              </Field>
            </Row>
          </Section>

          <Section title="Business Manager Details">
            <Row>
              <Field label="Created BM ID">
                <Input value={form.created_bm_id ?? ""} onChange={(e) => up("created_bm_id", e.target.value)} />
              </Field>
              <Field label="Received BM ID">
                <Input value={form.received_bm_id ?? ""} onChange={(e) => up("received_bm_id", e.target.value)} />
              </Field>
            </Row>
            <Row>
              <Field label="Received Access ID">
                <Input value={form.received_bm_access_id ?? ""} onChange={(e) => up("received_bm_access_id", e.target.value)} />
              </Field>
              <Field label="Access Type">
                <Select value={form.access_type ?? "none"} onValueChange={(v) => up("access_type", v === "none" ? null : v as never)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {META_ACCESS_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </Row>
          </Section>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={save} disabled={saving}>
            {saving ? "Saving…" : existing ? "Save Changes" : "Add Account"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function Row({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>;
}
function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}

function ManageAccessDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const { data: granted = [] } = useQuery({
    queryKey: ["meta-ids-access-list"],
    queryFn: listMetaAccess,
  });

  const [search, setSearch] = useState("");
  const { data: users = [] } = useQuery({
    queryKey: ["meta-ids-user-search", search],
    queryFn: async () => {
      const q = supabase.from("profiles").select("id, full_name, email, department").eq("status", "active").limit(20);
      const r = search.trim()
        ? await q.or(`full_name.ilike.%${search.trim()}%,email.ilike.%${search.trim()}%`)
        : await q;
      if (r.error) throw r.error;
      return r.data as { id: string; full_name: string | null; email: string | null; department: string | null }[];
    },
  });

  const grantMut = useMutation({
    mutationFn: grantMetaAccess,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["meta-ids-access-list"] });
      toast.success("Access granted");
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const revokeMut = useMutation({
    mutationFn: revokeMetaAccess,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["meta-ids-access-list"] });
      toast.success("Access revoked");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const grantedIds = new Set(granted.map((g) => g.user_id));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Manage Meta IDs Access</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs">Granted users ({granted.length})</Label>
            <div className="mt-1 rounded border border-border divide-y max-h-[200px] overflow-y-auto">
              {granted.length === 0 && (
                <div className="px-3 py-2 text-sm text-muted-foreground">Only admins have access.</div>
              )}
              {granted.map((g) => (
                <div key={g.user_id} className="flex items-center justify-between px-3 py-2 text-sm">
                  <div>
                    <div className="font-medium">{g.full_name ?? "—"}</div>
                    <div className="text-xs text-muted-foreground">{g.email}</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => revokeMut.mutate(g.user_id)}>
                    <X className="h-4 w-4 text-rose-600" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs">Add user</Label>
            <Input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email…" className="mt-1"
            />
            <div className="mt-1 rounded border border-border divide-y max-h-[220px] overflow-y-auto">
              {users.filter((u) => !grantedIds.has(u.id)).map((u) => (
                <div key={u.id} className="flex items-center justify-between px-3 py-2 text-sm">
                  <div>
                    <div className="font-medium">{u.full_name ?? "—"}</div>
                    <div className="text-xs text-muted-foreground">{u.email} · {u.department ?? ""}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => grantMut.mutate(u.id)}>
                    <UserPlus className="h-3.5 w-3.5 mr-1" /> Grant
                  </Button>
                </div>
              ))}
              {users.filter((u) => !grantedIds.has(u.id)).length === 0 && (
                <div className="px-3 py-2 text-sm text-muted-foreground">No users found.</div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// silence unused warning for imports kept for future use
void insertEncryptedMetaAccount;
void Badge;
